import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/api/projects'
import { promptsApi } from '@/api/prompts'
import { useTenantKey } from './useTenantKey'
import type { CreatePromptRequest, UpdatePromptRequest } from '@/types'

export function useProjectPhases(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'project-phases'),
    queryFn: () => projectsApi.getProjectPhases(projectId),
    enabled: !!projectId,
  })
}

export function usePrompts(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'prompts'),
    queryFn: () => promptsApi.getAll(projectId),
    enabled: !!projectId,
  })
}

export function usePrompt(projectId: string, promptId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'prompts'),
    queryFn: () => promptsApi.getAll(projectId),
    select: prompts => prompts.find(prompt => prompt.id === promptId),
    enabled: !!projectId && !!promptId,
  })
}

export function usePromptStats(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'prompts', 'stats'),
    queryFn: () => promptsApi.getStats(projectId),
    enabled: !!projectId,
  })
}

export function useCreatePrompt(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: CreatePromptRequest) =>
      promptsApi.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'prompts') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'prompts', 'stats') })
    },
  })
}

export function useUpdatePrompt(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromptRequest }) =>
      promptsApi.update(projectId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'prompts', 'stats') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'prompts') })
    },
  })
}

export function useDeletePrompt(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) => promptsApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'prompts') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'prompts', 'stats') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}
